import { AUTH_ROUTES, SPA_PATHS_OUTSIDE_V2 } from '@features/auth/constants';

const DEFAULT_DESTINATION = '/';

const MAX_DESTINATION_LENGTH = 2048;

const DENIED_DESTINATION_PATTERNS = [
  /^\/user\/log(in|out)(\/|$)/i,
  /^\/user\/\d+\/login\/tfa\/setup(\/|$)/i,
  /^\/system\/tfa(\/|$)/i,
  /^\/home(\/|$)/i,
];

const AUTH_PAGE_PATHS: readonly string[] = [
  ...SPA_PATHS_OUTSIDE_V2,
  '/user/password',
  '/v2/login',
  '/v2/logout',
  '/v2/dev-login',
  '/v2/reset',
  '/v2/k1',
];

const ABSOLUTE_URL_PATTERN = /^([a-z][a-z0-9+.-]*:|\/\/)/i;

const CONTROL_CHARACTER_REGEX = /\p{Cc}/u;

const NETWORK_PATH_PREFIX = '//';

const DRUPAL_PATH_QUERY_PARAM = 'q';

const hasUnsafeCharacters = (value: string): boolean =>
  value.includes('://') || value.includes('\\') || CONTROL_CHARACTER_REGEX.test(value);

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const normalizeRelativePath = (path: string): string => (path.startsWith('/') ? path : `/${path}`);

const toSameOriginUrl = (destination: string): URL | null => {
  try {
    const url = new URL(normalizeRelativePath(destination), window.location.origin);

    return url.origin === window.location.origin ? url : null;
  } catch {
    return null;
  }
};

const isLoginRoute = (path: string): boolean => path === AUTH_ROUTES.LOGIN || path.startsWith(`${AUTH_ROUTES.LOGIN}/`);

const isAuthPagePath = (path: string): boolean =>
  AUTH_PAGE_PATHS.some((route) => path === route || path.startsWith(`${route}/`));

const isAllowedPath = (path: string): boolean => {
  const lowerCasePath = path.toLowerCase();

  return (
    !isLoginRoute(lowerCasePath)
    && !isAuthPagePath(lowerCasePath)
    && !DENIED_DESTINATION_PATTERNS.some((pattern) => pattern.test(lowerCasePath))
  );
};

const isAllowedPathname = (pathname: string): boolean => {
  try {
    const decodedPathname = decodeURIComponent(pathname);

    return (
      !decodedPathname.startsWith(NETWORK_PATH_PREFIX)
      && !hasUnsafeCharacters(decodedPathname)
      && isAllowedPath(decodedPathname)
    );
  } catch {
    return false;
  }
};

const isAllowedDrupalQueryPath = (url: URL): boolean => {
  const drupalPath = url.searchParams.get(DRUPAL_PATH_QUERY_PARAM);

  return drupalPath === null || isAllowedPath(normalizeRelativePath(drupalPath));
};

/**
 * Validates a raw `?destination=` value into a safe, same-origin path, falling back to '/' for anything
 * empty, malformed, off-origin, or pointing back at an auth page.
 */
export const sanitizeDestination = (raw: string | null): string => {
  const destination = raw?.trim();

  if (!destination || destination.length > MAX_DESTINATION_LENGTH || ABSOLUTE_URL_PATTERN.test(destination)) {
    return DEFAULT_DESTINATION;
  }

  const decodedForChecks = safeDecode(destination);

  if (hasUnsafeCharacters(decodedForChecks)) {
    return DEFAULT_DESTINATION;
  }

  const firstSlashIndex = decodedForChecks.indexOf('/');
  const firstColonIndex = decodedForChecks.indexOf(':');

  if (firstColonIndex !== -1 && (firstSlashIndex === -1 || firstColonIndex < firstSlashIndex)) {
    return DEFAULT_DESTINATION;
  }

  const url = toSameOriginUrl(destination);

  if (
    !url
    || url.pathname.startsWith(NETWORK_PATH_PREFIX)
    || !isAllowedPathname(url.pathname)
    || !isAllowedDrupalQueryPath(url)
  ) {
    return DEFAULT_DESTINATION;
  }

  return `${url.pathname}${url.search}${url.hash}`;
};

const getPostAuthDestination = (): string =>
  sanitizeDestination(new URLSearchParams(window.location.search).get('destination'));

export default getPostAuthDestination;
